/** @jsxImportSource @emotion/react */
import { useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlayButton } from './PlayButton';
import { StyledVideoAspect, StyledVideoContainer, StyledVideoOverlay } from './styled';
import { fetchVideoMetadata } from './utils';
import dynamic from 'next/dynamic';
import { Schema, SchemaTypes } from '~/shared/schema';
import { VideoProps } from './types';

export type { PlayButtonProps, VideoProps } from './types';

const VideoPlayerLazy = dynamic(() => import('react-player/lazy'), { ssr: false });

const defaultEmbedOptions = {
    youtube: {
        embedOptions: {
            rel: 0,
            modestbranding: 1,
        },
    },
};

export const Video = ({
    controls = true,
    muted = false,
    loop = false,
    playing = false,
    cover = false,
    src,
    posterSrc,
    repositionKey,
    onPlay,
    onPause,
    embedConfig = defaultEmbedOptions,
    playButton: VideoPlayButton,
    aspectRatio: defaultAspectRatio,
    showPlayButtonText = true,
}: VideoProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [aspectRatio, setAspectRatio] = useState(defaultAspectRatio);
    const [isPlaying, setIsPlaying] = useState(playing);
    const [initialPlay, setInitialPlay] = useState(playing);
    const [thumbnailSrc, setThumbnailSrc] = useState(posterSrc);
    const [videoTitle, setVideoTitle] = useState<string>();
    const isInView = useInView(ref);

    const styles = useMemo(
        () => ({
            backgroundImage: isInView && thumbnailSrc ? `url(${thumbnailSrc})` : '',
            // Fallback to 16/9
            paddingTop: cover ? 0 : aspectRatio ? `${aspectRatio * 100}%` : '56.25%',
        }),
        [isInView, thumbnailSrc, cover, aspectRatio],
    );

    const onPlayHandler = () => {
        setInitialPlay(true);
        setIsPlaying(true);
        onPlay?.();
    };

    const onPauseHandler = () => {
        setIsPlaying(false);
        onPause?.();
    };

    const onTriggerHandler = () => {
        isPlaying ? onPauseHandler() : onPlayHandler();
    };

    /**
     * Only reposition if covering. Otherwise fluid
     */
    useEffect(() => {
        if (!aspectRatio || !cover) {
            return;
        }

        /**
         * Determine if stretch horizontal or vertical
         */
        const scaleVideo = () => {
            const aspectElement = ref?.current;

            if (aspectElement) {
                const parentElement = aspectElement.parentElement as HTMLDivElement;
                const { width, height } = parentElement.getBoundingClientRect();

                const parentRatio = height / width;
                const verticalScale = parentRatio > aspectRatio;

                if (verticalScale) {
                    aspectElement.style.height = '100%';
                    aspectElement.style.width = '1000%';
                } else {
                    aspectElement.style.width = '100%';
                    aspectElement.style.height = '1000%';
                }
            }
        };

        scaleVideo();
        window.addEventListener('resize', scaleVideo);
        return () => window.removeEventListener('resize', scaleVideo);
    }, [aspectRatio, cover, repositionKey]);

    useEffect(() => {
        if (!aspectRatio || !thumbnailSrc) {
            fetchVideoMetadata(src).then(
                ({ aspectRatio: newAspectRatio, title, thumbnailSrc: newThumbnailSrc }) => {
                    aspectRatio || setAspectRatio(newAspectRatio);
                    videoTitle || setVideoTitle(title);
                    thumbnailSrc || setThumbnailSrc(newThumbnailSrc);
                },
            );
        } else if (!videoTitle) {
            fetchVideoMetadata(src).then(({ title }) => {
                videoTitle || setVideoTitle(title);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        playing ? onPlayHandler() : onPauseHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing]);

    return (
        <StyledVideoContainer css={styles} cover={cover} playing={isPlaying}>
            <Schema
                type={SchemaTypes.Video}
                props={{ contentUrl: src, thumbnailUrl: thumbnailSrc, name: videoTitle }}
            />

            <StyledVideoAspect ref={ref}>
                {initialPlay ? (
                    <VideoPlayerLazy
                        key={src}
                        url={src}
                        playing={isPlaying}
                        controls={isPlaying && controls}
                        muted={muted}
                        loop={loop}
                        onPause={onPauseHandler}
                        onPlay={onPlayHandler}
                        config={embedConfig}
                    />
                ) : null}
            </StyledVideoAspect>

            <StyledVideoOverlay playing={isPlaying} onClick={onTriggerHandler} />

            {VideoPlayButton ? (
                <VideoPlayButton playing={isPlaying} onClick={onTriggerHandler} />
            ) : (
                <PlayButton
                    playing={isPlaying}
                    onClick={onTriggerHandler}
                    showText={showPlayButtonText}
                />
            )}
        </StyledVideoContainer>
    );
};

export default Video;
